// src/components/HSCodeDetail.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import HSCodeUpdateSingle from "./HSCodeUpdateSingle";
import {
  X,
  ChevronDown,
  ChevronUp,
  Tag as TagIcon,
  ShieldCheck,
  Percent,
  Info,
} from "lucide-react";

/**
 * Modernized HSCode Detail Overlay
 * - Pure Tailwind styling (no external CSS)
 * - ESC / backdrop close, focus management
 * - Compact accordions, subtle elevation, RTL-aware
 * - Works with your Redux shape (data, loading, error)
 */
export default function HSCodeDetail({ onClose }) {
  const [isClosing, setIsClosing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [commercialsOpen, setCommercialsOpen] = useState(false);

  const { loading, data, error } = useSelector(
    (state) => state.hscode || { loading: false, data: null, error: null }
  );
  const { role } = useSelector((state) => state.auth.user || { role: null });

  const dialogRef = useRef(null);
  const close = useCallback(() => setIsClosing(true), []);

  // Handle closing animation
  useEffect(() => {
    if (!isClosing) return;
    const t = setTimeout(() => onClose?.(), 200);
    return () => clearTimeout(t);
  }, [isClosing, onClose]);

  // ESC key to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  // Focus the dialog on mount for accessibility
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const dutyPercent = useMemo(() => {
    if (!data) return 0;
    const p = Number(data.profit) || 0;
    const d = Number(data.customs_duty_rate) || 0;
    return p + d;
  }, [data]);

  const dutyBadgeStyle = useMemo(() => {
    if (dutyPercent >= 20)
      return { bg: "bg-red-100", text: "text-red-700", ring: "ring-red-200" };
    if (dutyPercent >= 10)
      return {
        bg: "bg-blue-100",
        text: "text-blue-700",
        ring: "ring-blue-200",
      };
    return {
      bg: "bg-emerald-100",
      text: "text-emerald-700",
      ring: "ring-emerald-200",
    };
  }, [dutyPercent]);

  // Group tags by title
  const groupedTags = useMemo(() => {
    if (!data?.tags?.length) return [];
    const map = new Map();
    for (const t of data.tags) {
      const title = t.title ?? "—";
      const val = t.tag ?? "";
      if (!map.has(title)) map.set(title, []);
      map.get(title).push(val);
    }
    return Array.from(map.entries()); // [ [title, [tags]] ]
  }, [data]);

  return (
    <div
      dir="rtl"
      className={`fixed inset-0 z-50 ${isClosing ? "pointer-events-none" : ""}`}
      aria-modal="true"
      role="dialog"
      aria-labelledby="hscode-detail-title"
      onClick={close}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity ${
          isClosing ? "opacity-0" : "opacity-100"
        }`}
      />

      {/* Panel */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`absolute right-0 top-0 h-full w-full sm:w-[560px] md:w-[680px] 
        bg-white shadow-2xl border-s transition-all outline-none
        ${
          isClosing ? "translate-x-full opacity-0" : "translate-x-0 opacity-100"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-white/80 backdrop-blur px-4 py-3 md:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2
                id="hscode-detail-title"
                className="text-lg md:text-xl font-bold text-gray-900 truncate"
                title={data?.goods_name_fa || data?.code || "جزئیات کد HS"}
              >
                {data ? data.goods_name_fa || "جزئیات کد HS" : "جزئیات کد HS"}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                {data?.code && (
                  <span className="font-mono text-gray-700">
                    HS: {data.code}
                  </span>
                )}
                {Number.isFinite(dutyPercent) && (
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg ring-1 ${dutyBadgeStyle.bg} ${dutyBadgeStyle.text} ${dutyBadgeStyle.ring}`}
                  >
                    <Percent className="w-3.5 h-3.5" />
                    {dutyPercent}%
                  </span>
                )}
                {data?.SUQ || data?.suq ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg ring-1 bg-indigo-50 text-indigo-700 ring-indigo-200">
                    واحد: {data.SUQ ?? data.suq}
                  </span>
                ) : null}
                {data?.priority ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg ring-1 bg-amber-50 text-amber-700 ring-amber-200">
                    اولویت {data.priority}
                  </span>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={close}
              aria-label="بستن"
              className="inline-flex items-center justify-center rounded-xl border bg-white px-2.5 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="h-full overflow-y-auto px-4 py-4 md:px-6 md:py-6">
          {/* Loading / Error */}
          {loading && (
            <div className="rounded-xl border bg-white p-4 text-sm text-gray-600 flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-500" />
              در حال بارگذاری جزئیات…
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              خطا: {String(error)}
            </div>
          )}

          {/* Content */}
          {data && (
            <div className="space-y-5">
              {/* Details (Accordion) */}
              <section className="rounded-2xl border bg-white shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 md:px-5 hover:bg-gray-50"
                >
                  <span className="text-sm font-semibold text-gray-900">
                    جزئیات کالا
                  </span>
                  {detailsOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {detailsOpen && (
                  <div className="px-4 pb-4 md:px-5 md:pb-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-1">
                        <div className="text-gray-500">کد تعرفه</div>
                        <div className="font-mono text-gray-900">
                          {data.code || "—"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-gray-500">نام فارسی</div>
                        <div className="text-gray-900">
                          {data.goods_name_fa || "—"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-gray-500">نام انگلیسی</div>
                        <div className="text-gray-900">
                          {data.goods_name_en || "—"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-gray-500">سود بازرگانی</div>
                        <div className="text-gray-900">
                          {data.profit ?? "—"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-gray-500">حقوق گمرکی</div>
                        <div className="text-gray-900">
                          {data.customs_duty_rate ?? "—"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-gray-500">واحد شمارش</div>
                        <div className="text-gray-900">
                          {data.SUQ ?? data.suq ?? "—"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-gray-500">فصل</div>
                        <div className="text-gray-900">
                          {data.season?.description || "—"}
                        </div>
                      </div>
                      {data.heading?.description && (
                        <div className="space-y-1">
                          <div className="text-gray-500">قسمت</div>
                          <div className="text-gray-900">
                            {data.heading.description}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>

              {/* Tags (Accordion) */}
              <section className="rounded-2xl border bg-white shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setTagsOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 md:px-5 hover:bg-gray-50"
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <TagIcon className="w-4 h-4 text-gray-500" />
                    برچسب‌ها
                  </span>
                  {tagsOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {tagsOpen && (
                  <div className="px-4 pb-4 md:px-5 md:pb-5">
                    {!groupedTags.length ? (
                      <div className="text-sm text-gray-500">
                        برچسبی ثبت نشده است.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {groupedTags.map(([title, tags]) => (
                          <div key={title} className="text-sm">
                            <div className="text-gray-600 mb-2 font-medium">
                              {title}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {tags.map((t, i) => (
                                <span
                                  key={`${title}-${t}-${i}`}
                                  className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Commercials / Licenses (Accordion) */}
              <section className="rounded-2xl border bg-white shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCommercialsOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 md:px-5 hover:bg-gray-50"
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <ShieldCheck className="w-4 h-4 text-gray-500" />
                    مجوزها و ملاحظات
                  </span>
                  {commercialsOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {commercialsOpen && (
                  <div className="px-4 pb-4 md:px-5 md:pb-5">
                    {!data?.commercials?.length ? (
                      <div className="text-sm text-gray-500">
                        موردی ثبت نشده است.
                      </div>
                    ) : (
                      <ul className="space-y-2 text-sm">
                        {data.commercials.map((c) => (
                          <li
                            key={c.id}
                            className="rounded-xl border px-3 py-2 bg-gray-50 text-gray-800"
                          >
                            <div className="font-medium text-gray-900">
                              {c.condition}
                            </div>
                            <div className="text-gray-700 mt-0.5">
                              {c.result}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </section>

              {/* Admin tools */}
              {role === "admin" && (
                <section className="rounded-2xl border bg-white shadow-sm overflow-hidden">
                  <div className="border-b px-4 py-3 md:px-5">
                    <h3 className="text-sm font-semibold text-gray-900">
                      به‌روزرسانی سریع (ادمین)
                    </h3>
                  </div>
                  <div className="p-4 md:p-5">
                    <HSCodeUpdateSingle code={data.code} />
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
